import { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { RefreshCw, Users, Search, Medal, Trophy, Gem } from 'lucide-react'
import { api } from '../lib/api'
import { formatTimezoneLabel } from '../lib/timezoneDisplay'
import { useAuthStore } from '../store/authStore'
import clsx from 'clsx'
import { NUDGE_CATEGORIES, CATEGORY_METADATA, type NudgeCategory } from '@duva/shared'

interface Member {
  id: string
  name: string
  email: string
  role: 'admin' | 'manager' | 'employee'
  preferred_channel: 'slack' | 'teams' | 'email' | null
  nudge_frequency: 'daily' | 'weekly' | 'off' | null
  nudge_track: string | null
  /** From API: manager may see channel/frequency/track only for self + duva_circles direct reports */
  nudge_fields_visible?: boolean
  /** IANA; empty = workspace default */
  timezone?: string | null
  slack_user_id: string | null
  completed_this_month: number
  /** ISO timestamp of most recent nudge delivery, or null */
  last_nudge_sent_at?: string | null
  /** Calculated client-side */
  rank?: number
}

const ROLE_STYLES: Record<string, string> = {
  admin: 'bg-[#EDE8F5] text-ink border-[#d4cce8]',
  manager: 'bg-[#EBF6FB] text-brand-teal border-[#a7d8e0]',
  employee: 'bg-warm-inset text-ink-muted border-warm-border',
}

const FREQ_COLORS: Record<string, string> = {
  daily: 'text-emerald-800',
  weekly: 'text-brand-teal',
  off: 'text-ink-soft',
}

const TRACK_OPTIONS = NUDGE_CATEGORIES

/** Whole days since `sentAt` (UTC instant), for admin team health. */
function daysSinceLastNudge(sentAt: string | null | undefined): number | null {
  if (sentAt == null || sentAt === '') return null
  const t = new Date(sentAt).getTime()
  if (Number.isNaN(t)) return null
  const ms = Date.now() - t
  if (ms < 0) return 0
  return Math.floor(ms / (24 * 60 * 60 * 1000))
}

function formatDaysSinceNudge(sentAt: string | null | undefined): string {
  if (sentAt == null || sentAt === '') return 'Never'
  const days = daysSinceLastNudge(sentAt)
  if (days === null) return '—'
  if (days === 0) return '0 days'
  if (days === 1) return '1 day'
  return `${days} days`
}

function memberDisplayName(m: { name: string; email: string }): string {
  const n = m.name?.trim()
  if (n && n !== 'undefined' && n !== 'null') return n
  const local = m.email?.split('@')[0]?.trim()
  if (local && local !== 'undefined' && local !== 'null') return local
  return 'Member'
}

const TRACK_STYLES: Record<string, string> = Object.entries(CATEGORY_METADATA).reduce(
  (acc, [cat, meta]) => ({
    ...acc,
    [cat]: meta.tailwind,
  }),
  {}
)

function canManageMemberNudgePrefs(m: Member, isAdmin: boolean): boolean {
  if (isAdmin) return true
  return m.nudge_fields_visible === true
}

export function TeamPage() {
  const { user } = useAuthStore()
  const isAdmin = user?.role === 'admin'
  const isManager = user?.role === 'manager' || isAdmin

  const [members, setMembers] = useState<Member[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [syncing, setSyncing] = useState(false)
  const [syncResult, setSyncResult] = useState<string | null>(null)
  const [trackSaving, setTrackSaving] = useState<string | null>(null) // userId being saved
  const [trackError, setTrackError] = useState<string | null>(null)
  const [tzSaving, setTzSaving] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  const avgCompletion = members.length > 0 
    ? members.reduce((s, m) => s + (m.completed_this_month ?? 0), 0) / members.length 
    : 0

  const isEliteOrg = avgCompletion >= 2.0

  /** Logged-in user’s timezone from /api/me (source of truth for “your” row). */
  const [myTimezone, setMyTimezone] = useState<string | null | undefined>(undefined)
  /** Cloudflare-detected tz for this browser (same for “Use my location” on your row). */
  const [myDetectedTz, setMyDetectedTz] = useState<string | null>(null)
  const [selfManualTzOpen, setSelfManualTzOpen] = useState(false)

  async function loadMembers(): Promise<Member[]> {
    setLoading(true)
    setLoadError(null)
    try {
      const r = await api.getMembers() as { members: Member[] }
      const list = Array.isArray(r.members) ? r.members : []
      
      // Calculate ranks based on completed_this_month
      const sorted = [...list].sort((a, b) => (b.completed_this_month || 0) - (a.completed_this_month || 0))
      const rankedList = list.map(m => {
        const rank = sorted.findIndex(s => s.id === m.id) + 1
        return { ...m, rank }
      })

      setMembers(rankedList)
      return rankedList
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Could not load team members'
      setLoadError(msg)
      setMembers([])
      return []
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadMembers()
  }, [])

  useEffect(() => {
    let cancelled = false
    api
      .getMe()
      .then((me) => {
        if (cancelled) return
        setMyTimezone(me.timezone ?? null)
        const d = me.detected_timezone?.trim()
        setMyDetectedTz(d && d.length > 0 ? d : null)
      })
      .catch(() => {
        if (!cancelled) {
          setMyTimezone(null)
          setMyDetectedTz(null)
        }
      })
    return () => {
      cancelled = true
    }
  }, [user?.id])

  function formatSyncMessage(res: { newMembers: number; processed?: number; totalMembers: number }): string {
    const { newMembers, processed = 0, totalMembers } = res
    if (newMembers > 0) {
      return `Added ${newMembers} new member(s). Total: ${totalMembers}.`
    }
    if (processed > 0) {
      return `Refreshed ${processed} member(s) from Slack. Total: ${totalMembers}.`
    }
    return `Team is up to date. Total: ${totalMembers}.`
  }

  async function handleSync() {
    setSyncing(true)
    setSyncResult(null)
    try {
      const res = await api.syncMembers() as { newMembers: number; processed?: number; totalMembers: number }
      setSyncResult(formatSyncMessage(res))
      let list = await loadMembers()
      if (res.totalMembers > 0 && list.length === 0) {
        await new Promise(r => setTimeout(r, 400))
        list = await loadMembers()
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Unknown error'
      setSyncResult(msg === 'Unauthorized' ? 'Sync failed — sign in again.' : `Sync failed — ${msg}`)
    } finally {
      setSyncing(false)
    }
  }

  async function handleTimezoneBlur(m: Member, raw: string) {
    const next = raw.trim() === '' ? null : raw.trim()
    const prev = (m.timezone ?? '').trim() || null
    if (next === prev) return
    setTzSaving(m.id)
    setTrackError(null)
    try {
      await api.updateMember(m.id, { timezone: next })
      if (m.id === user?.id) setMyTimezone(next)
      await loadMembers()
    } catch (e: unknown) {
      setTrackError(e instanceof Error ? e.message : 'Failed to update timezone')
    } finally {
      setTzSaving(null)
    }
  }

  async function applyMyConnectionTimezone() {
    if (!user?.id || !myDetectedTz?.trim()) return
    setTzSaving(user.id)
    setTrackError(null)
    try {
      await api.patchMe({ timezone: myDetectedTz.trim() })
      setMyTimezone(myDetectedTz.trim())
      await loadMembers()
    } catch (e: unknown) {
      setTrackError(e instanceof Error ? e.message : 'Failed to apply connection timezone')
    } finally {
      setTzSaving(null)
    }
  }

  async function clearMyTimezoneToDefault() {
    if (!user?.id) return
    setTzSaving(user.id)
    setTrackError(null)
    try {
      await api.patchMe({ timezone: null })
      setMyTimezone(null)
      await loadMembers()
    } catch (e: unknown) {
      setTrackError(e instanceof Error ? e.message : 'Failed to clear timezone')
    } finally {
      setTzSaving(null)
    }
  }

  async function handleTrackChange(memberId: string, track: string | null) {
    // Optimistic update
    setMembers((prev: Member[]) => prev.map((m: Member) => m.id === memberId ? { ...m, nudge_track: track } : m))
    setTrackSaving(memberId)
    setTrackError(null)
    try {
      await api.updateMember(memberId, { nudgeTrack: track })
    } catch (e: unknown) {
      // Revert on failure
      void loadMembers()
      setTrackError(e instanceof Error ? e.message : 'Failed to update track')
    } finally {
      setTrackSaving(null)
    }
  }

  const filteredMembers = members.filter((m: Member) => 
    m.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
    m.email.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const columns = [
    'Member',
    'Rank',
    'Role',
    ...(isManager ? ['Frequency', 'Nudge Track'] : []),
    'Timezone',
    'Connected'
  ] as const

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-ink" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>Team</h1>
          <p className="text-sm mt-1" style={{color: '#6b8a8f'}}>{members.length} members receiving nudges</p>
          {user && myTimezone !== undefined && (
            <p className="text-xs mt-2 text-ink-muted">
              <span className="font-medium text-ink-soft">Your timezone:</span>{' '}
              {formatTimezoneLabel(myTimezone)}
              {myTimezone == null || String(myTimezone).trim() === '' ? (
                <>
                  {' '}
                  — set it in{' '}
                  <NavLink to="/dashboard/settings" className="text-brand-teal font-medium underline underline-offset-2">
                    Settings
                  </NavLink>
                  .
                </>
              ) : null}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-initial">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint" size={14} />
            <input
              type="text"
              placeholder="Search members..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full sm:w-64 pl-9 pr-4 py-2 bg-warm-card border border-warm-border rounded-lg text-sm text-ink outline-none focus:border-brand-teal transition-colors shadow-card"
            />
          </div>
          {isAdmin && (
            <button
              onClick={handleSync}
              disabled={syncing}
              className="flex items-center gap-2 bg-warm-card hover:bg-warm-inset border border-warm-border text-ink font-medium text-sm px-4 py-2 rounded-lg transition-colors disabled:opacity-50 shadow-card whitespace-nowrap"
            >
              <RefreshCw size={14} className={clsx(syncing && 'animate-spin')} />
              {syncing ? 'Syncing…' : 'Sync'}
            </button>
          )}
        </div>
      </div>

      {syncResult && (
        <div
          className="mb-5 text-sm px-4 py-3 rounded-xl border text-ink-muted shadow-card"
          style={{ background: '#FDFCF8', borderColor: '#2A7A8A', boxShadow: '0 0 20px -8px rgba(42,122,138,0.2)' }}
        >
          {syncResult}
        </div>
      )}

      {loadError && (
        <div className="mb-5 text-sm px-4 py-3 rounded-xl border bg-[#fdf2f2] border-[#e8cfcf] text-red-800 flex flex-wrap items-center justify-between gap-3">
          <span>Could not load team list: {loadError}</span>
          <button
            type="button"
            onClick={() => void loadMembers()}
            className="text-xs font-medium px-3 py-1.5 rounded-lg border border-warm-border bg-warm-card hover:bg-warm-inset text-ink"
          >
            Retry
          </button>
        </div>
      )}

      {trackError && (
        <div className="mb-5 text-sm px-4 py-3 rounded-xl border bg-[#fdf2f2] border-[#e8cfcf] text-red-800">
          {trackError}
        </div>
      )}

      <div className="bg-warm-card border border-warm-border rounded-2xl overflow-hidden shadow-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-warm-border">
                {columns.map(h => (
                  <th key={h} className="text-left px-5 py-3 text-xs font-medium text-ink-soft">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className="border-b border-warm-border/60">
                    {Array.from({ length: columns.length }).map((_, j) => (
                      <td key={j} className="px-5 py-3.5">
                        <div className="h-3 bg-warm-inset rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
                : filteredMembers.map(m => {
                  const displayName = memberDisplayName(m)
                  const isSelf = m.id === user?.id
                  const effectiveTimezone = (() => {
                    if (!isSelf) return m.timezone?.trim() ? m.timezone.trim() : null
                    if (myTimezone === undefined) return m.timezone?.trim() ? m.timezone.trim() : null
                    if (myTimezone === null || String(myTimezone).trim() === '') return null
                    return String(myTimezone).trim()
                  })()
                  return (
                  <tr
                    key={m.id}
                    className="border-b border-warm-border/60 hover:bg-warm-inset/40 transition-colors"
                  >
                    {/* Member */}
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded-full bg-brand-teal flex items-center justify-center text-xs font-bold text-white shrink-0">
                          {displayName[0]?.toUpperCase() ?? '?'}
                        </div>
                        <div>
                          <p className="text-ink font-medium flex items-center gap-1.5">
                            {displayName}
                            {m.completed_this_month >= 4 && (
                              <span title={`${m.completed_this_month} actions this month`}>
                                <Medal 
                                  size={14} 
                                  className={clsx(
                                    m.completed_this_month >= 12 ? 'text-brand-teal' :
                                    m.completed_this_month >= 8 ? 'text-amber-500' : 'text-slate-400'
                                  )} 
                                />
                              </span>
                            )}
                          </p>
                          <p className="text-ink-soft text-xs">{m.email}</p>
                        </div>
                      </div>
                    </td>

                    {/* Rank */}
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-1.5">
                        <span className={clsx(
                          "text-sm font-bold tabular-nums",
                          m.rank === 1 ? "text-amber-600" : "text-ink-soft"
                        )}>
                          #{m.rank ?? '—'}
                        </span>
                        {m.rank === 1 && m.completed_this_month > 0 && (
                          <Trophy size={14} className="text-amber-500" />
                        )}
                      </div>
                      <p className="text-[10px] text-ink-faint uppercase font-bold tracking-tight">
                        {m.completed_this_month} done
                      </p>
                    </td>

                    {/* Role */}
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2">
                        <span className={clsx('text-xs font-medium px-2 py-0.5 rounded-full border capitalize', ROLE_STYLES[m.role])}>
                          {m.role}
                        </span>
                        {isEliteOrg && (m.role === 'admin' || m.role === 'manager') && (
                          <span title="Elite Facilitator — Leading a high-engagement habit culture" className="text-brand-teal animate-pulse">
                            <Gem size={14} />
                          </span>
                        )}
                      </div>
                    </td>

                    {isManager && (
                      <>
                        <td className="px-5 py-3.5">
                          {canManageMemberNudgePrefs(m, isAdmin) && m.nudge_frequency ? (
                            <span className={clsx('capitalize font-medium', FREQ_COLORS[m.nudge_frequency])}>
                              {m.nudge_frequency}
                            </span>
                          ) : (
                            <span className="text-ink-faint">—</span>
                          )}
                        </td>
                        <td className="px-5 py-3.5">
                          {canManageMemberNudgePrefs(m, isAdmin) ? (
                            <div className="relative">
                              <select
                                value={m.nudge_track ?? ''}
                                onChange={e => handleTrackChange(m.id, e.target.value || null)}
                                disabled={trackSaving === m.id}
                                className={clsx(
                                  'text-xs font-medium rounded-full px-2.5 py-1 border cursor-pointer appearance-none pr-6 transition-opacity focus:outline-none focus:ring-1 focus:ring-brand-teal/50',
                                  m.nudge_track
                                    ? (TRACK_STYLES[m.nudge_track] ?? 'bg-warm-inset text-ink-muted border-warm-border')
                                    : 'bg-warm-inset text-ink-faint border-warm-border',
                                  trackSaving === m.id && 'opacity-50'
                                )}
                                style={{ backgroundImage: 'none' }}
                                title="Assign nudge track"
                              >
                                <option value="">— Auto</option>
                                {TRACK_OPTIONS.map(t => (
                                  <option key={t} value={t} className="capitalize">{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                                ))}
                              </select>
                              <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-ink-faint text-[10px]">
                                ▾
                              </span>
                              {trackSaving === m.id && (
                                <span className="absolute -right-5 top-1/2 -translate-y-1/2 text-ink-soft text-xs">…</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-ink-faint">—</span>
                          )}
                        </td>
                      </>
                    )}

                    <td className="px-5 py-3.5 min-w-[9rem]">
                      {isManager && canManageMemberNudgePrefs(m, isAdmin) ? (
                        isSelf ? (
                          <div className="space-y-2 max-w-[14rem]">
                            <p className="text-[10px] text-ink-muted leading-snug">
                              {formatTimezoneLabel(effectiveTimezone)}
                            </p>
                            <div className="flex flex-col gap-1">
                              <button
                                type="button"
                                onClick={() => void applyMyConnectionTimezone()}
                                disabled={tzSaving === m.id || !myDetectedTz}
                                className="text-left text-[11px] font-medium px-2 py-1.5 rounded-lg border border-brand-teal/40 bg-brand-teal/5 text-brand-teal hover:bg-brand-teal/10 disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                Use my location (Cloudflare)
                              </button>
                              <button
                                type="button"
                                onClick={() => void clearMyTimezoneToDefault()}
                                disabled={tzSaving === m.id}
                                className="text-left text-[11px] font-medium px-2 py-1.5 rounded-lg border border-warm-border bg-warm-inset text-ink-muted hover:bg-warm-card disabled:opacity-50"
                              >
                                Workspace default
                              </button>
                            </div>
                            <button
                              type="button"
                              onClick={() => setSelfManualTzOpen((o) => !o)}
                              className="text-[10px] text-brand-teal font-medium hover:underline underline-offset-2"
                            >
                              {selfManualTzOpen ? 'Hide manual IANA' : 'Enter IANA manually'}
                            </button>
                            {selfManualTzOpen && (
                              <input
                                key={`tz-self-${effectiveTimezone ?? ''}-${myTimezone === undefined ? 'load' : 'ok'}`}
                                type="text"
                                defaultValue={effectiveTimezone ?? ''}
                                disabled={tzSaving === m.id}
                                onBlur={(e) => void handleTimezoneBlur(m, e.target.value)}
                                className={clsx(
                                  'w-full text-xs font-mono bg-warm-inset border border-warm-border rounded-lg px-2 py-1.5 text-ink placeholder:text-ink-faint focus:outline-none focus:border-brand-teal',
                                  tzSaving === m.id && 'opacity-50'
                                )}
                                placeholder="e.g. Europe/London"
                                spellCheck={false}
                                title="IANA timezone"
                              />
                            )}
                            {!myDetectedTz && (
                              <p className="text-[9px] text-ink-faint">
                                No Cloudflare detection (e.g. local dev) — use Settings or manual IANA.
                              </p>
                            )}
                          </div>
                        ) : (
                          <input
                            key={`tz-${m.id}-${effectiveTimezone ?? ''}-${myTimezone === undefined ? 'load' : 'ok'}`}
                            type="text"
                            defaultValue={effectiveTimezone ?? ''}
                            disabled={tzSaving === m.id}
                            onBlur={(e) => void handleTimezoneBlur(m, e.target.value)}
                            className={clsx(
                              'w-full max-w-[11rem] text-xs font-mono bg-warm-inset border border-warm-border rounded-lg px-2 py-1.5 text-ink placeholder:text-ink-faint focus:outline-none focus:border-brand-teal',
                              tzSaving === m.id && 'opacity-50'
                            )}
                            placeholder="IANA or empty"
                            spellCheck={false}
                            title="IANA timezone for this member (your connection detection only applies to you)"
                          />
                        )
                      ) : (
                        <span className="text-xs font-mono text-ink-muted">
                          {isSelf
                            ? effectiveTimezone
                              ? formatTimezoneLabel(effectiveTimezone)
                              : 'Workspace default'
                            : m.timezone?.trim()
                              ? formatTimezoneLabel(m.timezone)
                              : 'Workspace default'}
                        </span>
                      )}
                    </td>

                    {isManager && (
                      <td className="px-5 py-3.5">
                        <span
                          className="text-xs text-ink-muted tabular-nums"
                          title={
                            m.last_nudge_sent_at
                              ? `Last sent ${new Date(m.last_nudge_sent_at).toLocaleString()}`
                              : 'No nudge recorded for this member'
                          }
                        >
                          {formatDaysSinceNudge(m.last_nudge_sent_at)}
                        </span>
                      </td>
                    )}

                    {/* Connected */}
                    <td className="px-5 py-3.5">
                      {m.slack_user_id
                        ? <span className="text-xs text-emerald-800 font-medium">✓ Slack</span>
                        : <span className="text-xs text-ink-faint">—</span>}
                    </td>
                  </tr>
                  )
                })
              }
              {!loading && members.length > 0 && filteredMembers.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="px-5 py-10 text-center text-sm text-ink-muted">
                    No members match your search.
                  </td>
                </tr>
              )}
              {!loading && members.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="px-5 py-16 text-center">
                    <Users size={28} className="text-ink-faint mx-auto mb-3" />
                    <p className="text-ink-muted text-sm">No team members yet.</p>
                    <p className="text-ink-soft text-xs mt-1">Sync from Slack to import your workspace.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isAdmin && (
        <p className="mt-4 text-xs text-ink-faint">
          <span className="font-medium">Nudge Track:</span> Select a track to override AI assignment.
          Choose <em>Auto</em> to let Duva pick based on the member's engagement patterns.
          <span className="block mt-2">
            <span className="font-medium">Timezone:</span> For <em>you</em>, use Cloudflare location or manual IANA. For
            others, enter IANA (their region). Empty = workspace default.
          </span>
        </p>
      )}
      {user?.role === 'manager' && (
        <p className="mt-4 text-xs text-ink-faint">
          <span className="font-medium">Your circle:</span> Frequency, nudge track, and timezone edits apply only to{' '}
          <em>you</em> and people you&apos;ve added with Slack <code className="text-brand-teal">/circle</code>{' '}
          (<code className="text-brand-teal">/duva-circle</code>).
          Everyone else shows &quot;—&quot; here; you still see last nudge and Slack connection for the full team.
        </p>
      )}
    </div>
  )
}
